import { CatalogPartSitesDataGridApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartSitesDataGridApi";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { enumColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getPartTypeLabels } from "src/api/generated/erp/db/types/enums/partType.ts";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { getAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { CatalogPartSitePartsView } from "src/api/generated/erp/db/types/tables/catalogPartSitePartsView.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SitePartsPage } from "src/components/views/erp/parts/siteParts/SitePartsPage.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CatalogPartSitesDataGridProps {
	catalogPartId: number;
	refreshRef: RefreshableElementRef;
}

export const CatalogPartSitesDataGrid = ({ catalogPartId, refreshRef }: CatalogPartSitesDataGridProps) => {
	const { openDialog } = useGenericDialog();
	return (
		<ClientSideDataGridModel
			gridId={"7746802265D28D337C"}
			fetchData={() => CatalogPartSitesDataGridApi.getCatalogPartSites({ catalogPartId: catalogPartId })}
			initialParams={{}}
			refreshRef={refreshRef}
			getRows={(data) => data}
			getRowId={(row) => row.sitePartId}
			render={({ dataGridProps, onlySelectedRow }) => (
				<FormCrudDataGrid<CatalogPartSitePartsView>
					columns={[
						textColumn({
							field: "siteName",
							headerName: i18n.t("site"),
						}),
						enumColumn({
							field: "partType",
							headerName: i18n.t("type"),
							enumLabels: getPartTypeLabels(),
							width: 120,
						}),
						enumColumn({
							field: "acquisitionMethod",
							headerName: i18n.t("acquisition_method"),
							enumLabels: getAcquisitionMethodLabels(),
							width: 140,
						}),
						textColumn({
							field: "responsiblePersonName",
							headerName: i18n.t("responsible_person"),
						}),
					]}
					onRowDoubleClick={({ row }) => {
						openSitePart(row);
					}}
					actionBarComponents={
						<>
							<AsyncButton
								label={i18n.t("open")}
								icon={faLink}
								disabled={!onlySelectedRow}
								variant={"outlined"}
								onClick={() => {
									if (!onlySelectedRow) return;
									openSitePart(onlySelectedRow);
								}}
							/>
						</>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.parts.catalogPartSites.grid"),
						data: {
							catalogPartId: catalogPartId,
						},
						rowFields: ["sitePartId", "catalogPartId", "siteId", "partType", "acquisitionMethod"],
						selectedRowPathSegment: (row) => row.siteName,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);

	function openSitePart(row: CatalogPartSitePartsView) {
		openDialog({
			title: i18n.t("part"),
			content: <SitePartsPage sitePartId={row.sitePartId} />,
			agentContext: {
				key: defineAgentUiContextKey("erp.parts.sitePart.dialog"),
				title: i18n.t("part"),
				pathSegments: [row.siteName],
				data: {
					catalogPartId: row.catalogPartId,
					sitePartId: row.sitePartId,
					siteId: row.siteId,
				},
			},
		});
	}
};
